import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../shared/api/supabase-client';
import { partnerApi } from '../../shared/api/partner.api';
import { ROUTES } from '../../shared/config/routes';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner@2.0.3';

/**
 * Вход партнёра по ссылке-приглашению: логин и пароль он задаёт сам, и
 * сразу попадает на свою страницу подключения аккаунтов.
 */
export function PartnerJoinPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [invite, setInvite] = useState<{ name: string; used: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError('В ссылке нет приглашения'); return; }
    partnerApi.invite(token).then(setInvite).catch((e) => setError(e.message));
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== repeat) { toast.error('Пароли не совпадают'); return; }
    setBusy(true);
    try {
      const { login: saved } = await partnerApi.join(token, login, password);
      const { error: signInError } = await supabase.auth.signInWithPassword({ email: `${saved}@instafarm.internal`, password });
      if (signInError) throw signInError;
      navigate(ROUTES.PARTNER, { replace: true });
    } catch (err: any) {
      toast.error(err.message ?? 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>InstaFarm для партнёров</CardTitle>
          {invite && !invite.used && <CardDescription>Приглашение для «{invite.name}». Придумайте логин и пароль — по ним вы будете входить.</CardDescription>}
        </CardHeader>
        <CardContent>
          {error ? (
            // Ссылка битая, отозвана или без приглашения: дальше этой страницы
            // не пройти — подсказываем оба выхода.
            <div className="space-y-3">
              <p className="text-sm text-destructive">{error}</p>
              <p className="text-sm text-muted-foreground">
                Попросите у нас новую ссылку. Уже входили — войдите своим логином и паролем.
              </p>
              <Button className="w-full" onClick={() => navigate(ROUTES.LOGIN)}>Войти</Button>
            </div>
          ) : !invite ? (
            <p className="text-sm text-muted-foreground">Проверяем приглашение…</p>
          ) : invite.used ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">По этой ссылке уже вошли. Войдите своим логином и паролем.</p>
              <Button className="w-full" onClick={() => navigate(ROUTES.LOGIN)}>Войти</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="partner-login">Логин</Label>
                <Input id="partner-login" value={login} onChange={(e) => setLogin(e.target.value.toLowerCase())} required autoComplete="username" placeholder="латиница, цифры, точка, дефис" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="partner-password">Пароль</Label>
                <Input id="partner-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="partner-repeat">Пароль ещё раз</Label>
                <Input id="partner-repeat" type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required minLength={8} autoComplete="new-password" />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>{busy ? 'Создаём…' : 'Продолжить'}</Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
